import {HalImage} from "@cm/hal"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {AnyImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {Region} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {ReduceOperator, ReductionHelperHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/reduction-helper-hal"
import {
    DEFAULT_MAX_PAGES_PER_BATCH_DIM,
    forEachBatchRegion,
    tileRegionIntoBatches,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/tiled-region-fetch-hal"
import {hasBatching} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"

export type TiledReduceOperator = Exclude<ReduceOperator, "custom">

export type TiledReduceOpConfig = {
    operator: TiledReduceOperator
}

type BaseOperator = "sum" | "sum-square" | "min" | "max"

// The operator ReductionHelperHal is configured with to reduce ONE batch. mean/mean-square/root-mean-square
// must not carry their own normalizing postProcess here - it divides by whatever region it's given, which
// is correct for a single full-image call but wrong per batch (each batch may have a different, possibly
// edge-clipped, pixel count). Normalization instead happens once, externally, in `finalize` below.
const perBatchOperatorFor = (operator: TiledReduceOperator): BaseOperator => {
    switch (operator) {
        case "sum":
        case "mean":
            return "sum"
        case "sum-square":
        case "mean-square":
        case "root-mean-square":
            return "sum-square"
        case "min":
            return "min"
        case "max":
            return "max"
    }
}

// The operator used to combine per-batch partial results together. This must be the per-batch operator's
// *pairwise reduce function only*, with any preprocess stripped: a batch's sum-of-squares combines with
// another's via plain sum (not by squaring again, which reusing the sum-square-configured per-batch helper
// here would do), matching how min/max combine via min/max.
const combineOperatorFor = (perBatchOperator: BaseOperator): "sum" | "min" | "max" => (perBatchOperator === "sum-square" ? "sum" : perBatchOperator)

const needsNormalization = (operator: TiledReduceOperator): boolean => operator === "mean" || operator === "mean-square" || operator === "root-mean-square"

type PhysicalImageRef = ReturnType<ImageOpContextHal["createImagePhysical"]>

type OpCache = {
    channels: number
    batchCount: number
    partial: PhysicalImageRef // per-batch scratch (1x1); unused (available as finalize scratch) when batchCount === 1
    staging: PhysicalImageRef | null // batchCount x 1 strip of partials, tree-collapsed by combineHelper; null when batchCount === 1
    rawResult: PhysicalImageRef // un-normalized combined result (1x1), input to finalize
}

type OpState = {
    operator: TiledReduceOperator
    perBatchHelper: ReductionHelperHal
    combineHelper: ReductionHelperHal
    cache: OpCache | null
}

// Streams a source region through the atlas in page-budget-limited batches (tiled-region-fetch-hal.ts) and
// reduces it down to a 1x1 result per configured op, entirely on the GPU: each batch is fetched once and
// reduced by every op against the same resident data (so N simultaneous ops - e.g. sum and sum-of-squares
// for mean+variance - cost one atlas fetch, not N), with two-level (intra-batch pyramid + cross-batch
// pyramid over a staging strip) tree accumulation rather than a naive running sum, and no CPU readback
// anywhere. GPU resources (compiled reduction shaders, and the small scratch/staging images) are allocated
// once and cached across repeated `reduceAll` calls, reallocating only if the source's channel count or
// batch count actually changes between calls.
//
// Deliberately narrower than ReductionHelperHal: excludes "custom" (its preprocess may be
// position-dependent - e.g. find-correlation-peak encodes the absolute pixel coordinate of a correlation
// peak into its reduced value, which region-local batching would corrupt) and does not support patch-grid
// (batched) sources (a fundamentally different output shape - N results, not one; `reduceAll` throws if
// given one). Both stay on ReductionHelperHal's original single-full-region-fetch path in callers.
export class TiledReductionHelper {
    constructor(
        private readonly ctx: ImageOpContextHal,
        ops: TiledReduceOpConfig[],
        private readonly maxPagesPerBatchDim: number = DEFAULT_MAX_PAGES_PER_BATCH_DIM,
    ) {
        this.opStates = ops.map(({operator}) => {
            const perBatchOperator = perBatchOperatorFor(operator)
            return {
                operator,
                perBatchHelper: new ReductionHelperHal(ctx.halContext, {operator: perBatchOperator}),
                combineHelper: new ReductionHelperHal(ctx.halContext, {operator: combineOperatorFor(perBatchOperator)}),
                cache: null,
            }
        })
    }

    dispose() {
        this.opStates.forEach((state) => {
            state.perBatchHelper.dispose()
            state.combineHelper.dispose()
            this.releaseCache(state)
        })
    }

    async reduceAll(args: {sourceImage: AnyImageOpHalData; targets: HalImage[]; options?: UpdateImageOptions}): Promise<void> {
        const {sourceImage, targets, options} = args
        if (targets.length !== this.opStates.length) {
            throw new Error("TiledReductionHelper.reduceAll: targets must match the number of configured ops")
        }
        if (hasBatching(sourceImage.descriptor)) {
            throw new Error("TiledReductionHelper does not support patch-grid (batched) sources")
        }
        const region: Region = {x: 0, y: 0, width: sourceImage.descriptor.width, height: sourceImage.descriptor.height}
        const channels = sourceImage.descriptor.channels
        const batchCount = tileRegionIntoBatches(region, sourceImage.ctx.pageSize, this.maxPagesPerBatchDim).length
        this.opStates.forEach((state) => this.ensureCache(state, channels, batchCount))

        let index = 0
        await forEachBatchRegion(
            sourceImage,
            region,
            this.maxPagesPerBatchDim,
            (batchHalImage, batchRegion) => {
                const batchView = batchHalImage.createView({
                    type: "sub-image",
                    mipLevelOffset: 0,
                    region: batchRegion,
                })
                this.opStates.forEach((state) => {
                    const cache = state.cache!
                    if (batchCount === 1) {
                        state.perBatchHelper.reduce({
                            source: batchView,
                            target: cache.rawResult.ref,
                        })
                    } else {
                        state.perBatchHelper.reduce({
                            source: batchView,
                            target: cache.partial.ref,
                        })
                        this.ctx.blit({
                            sourceImage: cache.partial.ref,
                            targetImage: cache.staging!.ref,
                            targetOffset: {
                                x: index,
                                y: 0,
                            },
                        })
                    }
                })
                index++
            },
            options,
        )

        if (batchCount > 1) {
            this.opStates.forEach((state) => {
                const cache = state.cache!
                state.combineHelper.reduce({
                    source: cache.staging!.ref,
                    target: cache.rawResult.ref,
                })
            })
        }

        const pixelCount = region.width * region.height
        this.opStates.forEach((state, i) => this.finalize(state, pixelCount, targets[i]))
    }

    private finalize(state: OpState, pixelCount: number, target: HalImage) {
        const raw = state.cache!.rawResult.ref
        if (!needsNormalization(state.operator)) {
            this.ctx.blit({
                sourceImage: raw,
                targetImage: target,
            })
            return
        }
        if (state.operator === "root-mean-square") {
            // reuse the now-unused `partial` slot as pre-sqrt scratch rather than allocating a new temp
            const meanOfSquares = state.cache!.partial.ref
            this.ctx.arithmeticHelper.scale({
                operand: raw,
                scale: 1 / pixelCount,
                result: meanOfSquares,
            })
            this.ctx.arithmeticHelper.sqrt({
                operand: meanOfSquares,
                result: target,
            })
        } else {
            this.ctx.arithmeticHelper.scale({
                operand: raw,
                scale: 1 / pixelCount,
                result: target,
            })
        }
    }

    private ensureCache(state: OpState, channels: number, batchCount: number) {
        if (state.cache && state.cache.channels === channels && state.cache.batchCount === batchCount) {
            return
        }
        this.releaseCache(state)
        state.cache = {
            channels,
            batchCount,
            partial: this.ctx.createImagePhysical({
                width: 1,
                height: 1,
                channels,
            }),
            staging:
                batchCount > 1
                    ? this.ctx.createImagePhysical({
                          width: batchCount,
                          height: 1,
                          channels,
                      })
                    : null,
            rawResult: this.ctx.createImagePhysical({
                width: 1,
                height: 1,
                channels,
            }),
        }
    }

    private releaseCache(state: OpState) {
        state.cache?.partial.release()
        state.cache?.staging?.release()
        state.cache?.rawResult.release()
        state.cache = null
    }

    private readonly opStates: OpState[]
}
